import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { Control, FieldErrors } from 'react-hook-form';

import MONTHS from 'common/src/constants/months';

import { PERCENTAGES_DO_NOT_SUM_TO_100 } from './constants';
import PercentField from './PercentField';

import { FormValues } from '../AllocationTemplateForm';
import SubAccountDetailRow from '../SubAccountDetailRow';

const MonthlyPercentageDetails = ({
  control,
  validationErrors,
  handleRemoveRow,
  rows,
}: {
  control: Control<FormValues, any, FormValues>;
  validationErrors?: string[];
  handleRemoveRow: (subAccountId: string) => void;
  rows: SubAccountDetailRow[];
}) => {
  const byMonth = useMemo(() => {
    const map = new Map<
      number,
      { row: SubAccountDetailRow; index: number }[]
    >();
    rows.forEach((row, index) => {
      const month = row.budgetMonth ?? 0;
      if (!map.has(month)) map.set(month, []);
      map.get(month).push({ row, index });
    });
    return map;
  }, [rows]);

  return Array.from({ length: 12 }, (_, i) => {
    const monthEntries = byMonth.get(i) ?? [];
    const monthErrors =
      validationErrors?.filter((e) => e.includes(` for ${MONTHS[i]},`)) ?? [];

    return (
      <Accordion key={i} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">{MONTHS[i]}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account Number</TableCell>
                <TableCell align="right" width={160}>
                  Cost %
                </TableCell>
                <TableCell align="right" width={160}>
                  Usage %
                </TableCell>
                <TableCell width={56} />
              </TableRow>
            </TableHead>
            <TableBody>
              {monthEntries.map(({ row, index }) => (
                <TableRow key={row.subAccountId}>
                  <TableCell>{row.number}</TableCell>
                  <TableCell align="right">
                    <PercentField
                      control={control}
                      name={`details.${index}.costPercent`}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <PercentField
                      control={control}
                      name={`details.${index}.usagePercent`}
                    />
                  </TableCell>
                  <TableCell padding="none">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveRow(row.subAccountId)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {monthErrors.length > 0 && (
            <Stack alignItems="flex-end" sx={{ pt: 1 }}>
              <Typography variant="body2" color="error">
                {PERCENTAGES_DO_NOT_SUM_TO_100}
              </Typography>
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>
    );
  });
};

export default MonthlyPercentageDetails;
